import React, {useEffect} from 'react';
import {FlatList, Platform, StyleSheet, TouchableOpacity, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {Icon} from '../../components/Icon';
import LoadingImg from '../../components/LoadingImg';
import ContentView from '../../components/ui-controls/layout/ContentView';
import Text from '../../components/ui-controls/Text';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import {OrderResponse} from '../../transport/apiServiceResponse';
import {toAUDAbs} from '../../utils/functions';

function ItemSeparatorComponent() {
  return <View style={styles.separator} />;
}

const RecentOrderScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.ACCOUNT_RECENT_ORDERS>) => {
    const {cartStore, userStore} = useStores();

    useEffect(() => {
      if (userStore.isLoggedIn) cartStore.loadRecentOrders();
    }, [cartStore, userStore.isLoggedIn]);

    const onPress = (item: OrderResponse) =>
      navigation.navigate(AppRoute.ACCOUNT_ORDER_VIEW, {
        orderNumber: item.order_number,
      });

    const getStatus = (item: OrderResponse) => {
      if (item.cancelled) {
        return (
          <View style={styles.cancelledHolder}>
            <Text style={styles.statusText}>CANCELLED</Text>
          </View>
        );
      } else if (item.checkout?.succeeded) {
        return (
          <View style={styles.completeHolder}>
            <Text style={styles.statusText}>COMPLETE</Text>
          </View>
        );
      } else if (item.checkout?.failed || item.checkout?.in_doubt) {
        return (
          <View style={styles.failHolder}>
            <Text style={styles.statusText}>PROBLEM</Text>
          </View>
        );
      } else if (
        !(item as OrderResponse & {online_payments?: {succeeded: boolean}}).online_payments
          ?.succeeded
      ) {
        return (
          <View style={styles.cancelledHolder}>
            <Text style={styles.statusText}>PAYMENT ISSUE</Text>
          </View>
        );
      } else {
        return <View />;
      }
    };

    const renderItem = ({item}: {item: OrderResponse}) => (
      <TouchableOpacity onPress={() => onPress(item)} style={styles.item}>
        <View style={styles.itemInside}>
          <Text style={styles.itemText}>Order #{item.order_number}</Text>
          <Text style={[styles.itemText, styles.subtle]}>
            {item.time_slot?.delivery_zone?.name || ''}
          </Text>
          <View>
            <Text style={styles.itemText}>{toAUDAbs(item.total.cents_inc_tax, true)}</Text>
            {getStatus(item)}
          </View>
        </View>
        <Icon name="chevron-right" size={12} color="grey" />
      </TouchableOpacity>
    );

    const renderItemSeparatorComponent = () => <ItemSeparatorComponent />;

    return (
      <ContentView>
        <FlatList
          data={cartStore.recentOrders}
          keyExtractor={item => String(item.order_number)}
          renderItem={renderItem}
          style={styles.flatList}
          contentContainerStyle={styles.contentContainer}
          ItemSeparatorComponent={renderItemSeparatorComponent}
          ListEmptyComponent={() =>
            cartStore.loadingRecentOrders ? (
              <LoadingImg />
            ) : (
              <View
                style={{
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  height: 200,
                }}>
                <Text
                  style={{
                    fontSize: 16,
                    fontWeight: '500',
                    color: theme.colour.text.heading.primary,
                  }}>
                  NO PURCHASE HISTORY FOUND
                </Text>
              </View>
            )
          }
        />
      </ContentView>
    );
  },
);

export default RecentOrderScreen;

const styles = StyleSheet.create({
  item: {
    paddingVertical: 16,
    marginHorizontal: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemInside: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flex: 1,
  },
  itemText: {
    fontSize: 16,
    color: 'black',
    flex: 1,
    textAlign: 'center',
  },
  subtle: {
    color: 'grey',
  },
  flatList: {
    flex: 1,
  },
  contentContainer: {
    ...Platform.select({
      ios: {},
      web: {
        flex: 1,
      },
    }),
  },
  separator: {
    borderBottomWidth: 1,
    borderBottomColor: 'lightgrey',
    marginHorizontal: 8,
  },
  activity: {
    justifyContent: 'center',
  },
  cancelledHolder: {
    backgroundColor: 'orange',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  completeHolder: {
    backgroundColor: theme.colour.background.general.primary,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  failHolder: {
    backgroundColor: 'red',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 4,
    flex: 1,
  },
  statusText: {
    color: 'white',
  },
  cancelText: {
    color: 'white',
    fontSize: 12,
  },
});
